import React, { useEffect, useState } from "react";
import { FiPlus, FiArrowRight } from "react-icons/fi";
import { GoFileDirectory, GoCalendar } from "react-icons/go";
import { toast } from "react-toastify";
import NovoRelatorioModal from "../modals/NovoRelatorioModal";
import RelatorioDetalhe from "./RelatorioDetalhe";
import { SEMANAS_MOCK } from "../mockDataSemanal";
import plcomService from "../../../services/plcomService";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function competenciaLabel(iso) {
  if (!iso) return "—";
  const [y, m] = iso.split("-").map(Number);
  return `${MESES[m - 1]}/${y}`;
}

/** Relatório mock já criado — 3 das 4 semanas de setembro/2026 geradas a
 * partir dos XMLs, para o quadrado pontilhado "Novo relatório semanal"
 * ficar visível no detalhe. */
const RELATORIO_MOCK = {
  id: "mock-0926",
  competencia: "2026-09-01",
  semanas: SEMANAS_MOCK.slice(0, 3),
  xmls: 63,
  criadoEm: "2026-09-30",
  status: "em_andamento",
  mock: true,
};

/** Doc da API (índice plcom_relatorios) → shape usado pelos cards/detalhe. */
function docParaRelatorio(doc) {
  return {
    id: doc.id,
    competencia: doc.competencia, // "2026-09"
    semanas: doc.semanas || [],
    xmls: doc.xmlCount || 0,
    criadoEm: doc.criadoEm,
    status: doc.status || "em_andamento",
    avisos: doc.avisos || [],
    api: true,
  };
}

/**
 * Aba "Revisor SPED's" — tela inicial com os relatórios de apuração
 * (mesmo padrão de cards da tela Revisor SPED). "Novo relatório" abre o
 * modal de período + dropzone de XMLs; criar gera um card que abre o
 * detalhe (semanas, mês consolidado, resumo e impostos).
 */
export default function RevisorTab() {
  const [relatorios, setRelatorios] = useState([RELATORIO_MOCK]);
  const [modalOpen, setModalOpen] = useState(false);
  const [aberto, setAberto] = useState(null); // relatório em detalhe

  const relatorioAberto = relatorios.find((r) => r.id === aberto);

  const carregar = () => {
    plcomService
      .listar()
      .then((docs) => {
        setRelatorios([RELATORIO_MOCK, ...docs.map(docParaRelatorio)]);
      })
      .catch(() => { /* API indisponível — mantém só o mock */ });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(carregar, []);

  const criarRelatorio = async ({ competencia, xmls }) => {
    if (xmls.length === 0) {
      // sem XMLs: relatório local vazio (fluxo demonstrativo)
      const novo = {
        id: `rel-${Date.now()}`,
        competencia,
        semanas: [],
        xmls: 0,
        criadoEm: new Date().toISOString(),
        status: "em_andamento",
      };
      setRelatorios((prev) => [novo, ...prev]);
      toast.success(`Relatório de ${competenciaLabel(competencia)} criado — gere as semanas subindo os XMLs.`);
      setAberto(novo.id);
      return;
    }

    try {
      toast.info(`Enviando ${xmls.length} arquivo(s) para apuração...`);
      const res = await plcomService.uploadXmls({ files: xmls, competencia });
      const novo = {
        id: res.relatorioId,
        competencia,
        semanas: [],
        xmls: xmls.length,
        criadoEm: new Date().toISOString(),
        status: "processando",
        api: true,
      };
      setRelatorios((prev) => [novo, ...prev]);
      setAberto(novo.id); // detalhe faz polling até o worker concluir
    } catch (e) {
      toast.error(e.response?.data?.error || "Falha ao enviar XMLs para apuração.");
    }
  };

  const atualizarRelatorio = (id, patch) =>
    setRelatorios((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );

  if (relatorioAberto) {
    return (
      <RelatorioDetalhe
        key={relatorioAberto.id}
        relatorio={relatorioAberto}
        onVoltar={() => setAberto(null)}
        onUpdate={(patch) => atualizarRelatorio(relatorioAberto.id, patch)}
        onRefresh={carregar}
      />
    );
  }

  return (
    <>
      <div className="section-title">
        <GoFileDirectory />
        <h4>Revisor SPED's — Relatórios de apuração</h4>
        <button
          type="button"
          className="btn-primary section-cta"
          onClick={() => setModalOpen(true)}
        >
          <FiPlus /> Novo relatório
        </button>
      </div>

      <div className="relatorio-grid">
        {relatorios.map((r) => (
          <button
            key={r.id}
            type="button"
            className="relatorio-card"
            onClick={() => setAberto(r.id)}
          >
            <div className="rc-head">
              <span className={`rc-status ${r.status}`}>
                {r.status === "concluido" ? "Concluído" : r.status === "processando" ? "Processando…" : r.status === "erro" ? "Erro" : "Em andamento"}
              </span>
              {r.mock && <span className="rc-mock">demo</span>}
              <GoCalendar className="rc-icon" />
            </div>
            <span className="rc-periodo">{competenciaLabel(r.competencia)}</span>
            <span className="rc-sub">Apuração semanal PLCom</span>
            <div className="rc-meta">
              <span>{r.semanas.length}/4 semanas</span>
              <span>{r.xmls} XMLs</span>
            </div>
            <span className="rc-open">
              Abrir relatório <FiArrowRight />
            </span>
          </button>
        ))}
      </div>

      <NovoRelatorioModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCriar={criarRelatorio}
      />
    </>
  );
}
